import LaunchIcon from "@mui/icons-material/Launch";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import cv from "../asset/file/Umairatul_Asyiqin_Resume_2026.pdf";
import background from "../asset/img/crumplePaper2.jpg";
import header2 from "../asset/img/headerImg2.png";
import ljmu from "../asset/img/ljmu.png";
import reinventionBadge from "../asset/img/reinvention-with-agentic-ai.png";
import awsBadge from "../asset/img/aws-certified-cloud-practitioner.png";

// ─── Data ────────────────────────────────────────────────────────────────────

const phrases = [
  "React Experiences",
  "Mobile Applications",
  "AI-Powered Tools",
  "Clean Interfaces",
];

const skillCategories = [
  {
    label: "Frontend",
    items: ["React.js", "React Native", "Next.js", "TypeScript"],
  },
  { label: "AI", items: ["Claude API (Anthropic)", "GitHub Copilot"] },
  { label: "Cloud & Deploy", items: ["Vercel", "Cloudflare"] },
  { label: "Version Control", items: ["Git", "GitHub"] },
  { label: "Methodology", items: ["Agile", "Scrum"] },
  {
    label: "Others",
    items: [
      "Angular",
      "Nodejs",
      "MUI",
      "Tailwind CSS",
      "Flutter",
      "AWS",
      "Docker",
      "Robot framework",
      "Agentic AI",
    ],
    overflow: 20,
  },
  { label: "Language", items: ["English", "Malay"] },
];

const stats = [
  { value: "4+", label: "Years" },
  { value: "5+", label: "Projects" },
  { value: "∞", label: "Learning" },
];

const LINE_H = "2.3rem";

const education = [
  {
    img: ljmu,
    school: "Liverpool John Moores University",
    major: "e-Business Technology & Management",
    award: "BSc(Hons) — Class 1 (73%)",
    period: "2018 – 2021",
  },
];

const certifications = [
  {
    credlyImg: awsBadge,
    name: "AWS Cloud Practitioner",
    issuer: "issuer from Amazon Web Services (AWS)",
    year: "2026",
    link: "https://www.credly.com/badges/1c94191c-85f3-4697-9ee2-9b6ca1d29526/public_url",
  },
  {
    credlyImg: reinventionBadge,
    name: "Reinvention with Agentic AI",
    issuer: "issuer from Accenture Technology",
    year: "2026",
    link: "https://www.credly.com/badges/84b3cc94-17f9-4365-a8d8-8a96e0356171/public_url",
  },
];

// ─── Typewriter hook ──────────────────────────────────────────────────────────

function useTypewriter(
  phrases,
  typingSpeed = 68,
  deletingSpeed = 38,
  pauseMs = 1900,
) {
  const [display, setDisplay] = useState("");
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const current = phrases[phraseIdx];
    let t;
    if (!isDeleting && display === current) {
      t = setTimeout(() => setIsDeleting(true), pauseMs);
    } else if (isDeleting && display === "") {
      setIsDeleting(false);
      setPhraseIdx((i) => (i + 1) % phrases.length);
    } else {
      t = setTimeout(
        () =>
          setDisplay(
            isDeleting
              ? current.slice(0, display.length - 1)
              : current.slice(0, display.length + 1),
          ),
        isDeleting ? deletingSpeed : typingSpeed,
      );
    }
    return () => clearTimeout(t);
  }, [
    display,
    isDeleting,
    phraseIdx,
    phrases,
    typingSpeed,
    deletingSpeed,
    pauseMs,
  ]);

  return display;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles()((theme) => ({
  // ── Hero section ──────────────────────────────────────────────────────────
  heroSection: {
    position: "relative",
    zIndex: 1,
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1.5rem",

    [theme.breakpoints.down("sm")]: {
      padding: 0,
    },
  },

  paperCard: {
    backgroundColor: "white",
    backgroundImage: `url(${background})`,
    position: "relative",
    overflow: "hidden",

    // Mobile: full screen
    width: "100%",
    minHeight: "100vh",
    padding: "3.5rem 2rem",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",

    // Desktop: card (plain white, no texture)
    [theme.breakpoints.up("sm")]: {
      width: "900px",
      minHeight: "auto",
      backgroundImage: "none",
      backgroundColor: "#ffffff",
      borderRadius: "14px",
      border: "1.5px dashed rgba(105,126,80,0.4)",
      boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
      padding: "3rem 3.5rem",
    },
  },

  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    fontSize: "0.7rem",
    fontFamily: "'Patrick Hand', cursive",
    letterSpacing: "0.13em",
    textTransform: "uppercase",
    color: "#3A5202",
    border: "1px dashed rgba(105,126,80,0.55)",
    borderRadius: "20px",
    padding: "4px 14px",
    width: "fit-content",
  },

  dot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    backgroundColor: "#5a8a2f",
    flexShrink: 0,
    animation: "$pulse 2.2s ease-in-out infinite",
  },

  headlineStatic: {
    fontFamily: "'Patrick Hand', cursive",
    fontWeight: 700,
    color: "#3A5202",
    lineHeight: 1.15,
    fontSize: "1.75rem",
  },

  headlineDynamic: {
    fontFamily: "'Patrick Hand', cursive",
    fontWeight: 700,
    color: "#697e50",
    lineHeight: 1.15,
    fontSize: "1.75rem",
    minHeight: "2rem",
    display: "block",
  },

  cursor: {
    display: "inline-block",
    width: "2px",
    height: "0.8em",
    backgroundColor: "#A10142",
    marginLeft: "3px",
    verticalAlign: "middle",
    animation: "$blink 0.85s step-end infinite",
  },

  bioText: {
    fontFamily: "'Patrick Hand', cursive",
    color: "rgba(0,0,0,0.6)",
    lineHeight: 1.65,
    fontSize: "0.95rem",
    fontStyle: "italic",
    borderLeft: "2px solid rgba(105,126,80,0.3)",
    paddingLeft: "0.85rem",
  },

  verticalDivider: {
    width: "1px",
    background:
      "linear-gradient(to bottom, transparent, rgba(105,126,80,0.35) 20%, rgba(105,126,80,0.35) 80%, transparent)",
    alignSelf: "stretch",
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: { display: "none" },
  },

  // ── Image (right col) ──────────────────────────────────────────────────────
  imageSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "1.4rem",
    height: "100%",
  },

  imageWrapper: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  imageRing: {
    position: "absolute",
    width: "200px",
    height: "200px",
    borderRadius: "50%",
    border: "1.5px dashed rgba(105,126,80,0.45)",
    zIndex: 1,
    animation: "$spin 22s linear infinite",
    [theme.breakpoints.down("sm")]: { width: "160px", height: "160px" },
  },

  imageRingInner: {
    position: "absolute",
    width: "175px",
    height: "175px",
    borderRadius: "50%",
    border: "1px dashed rgba(161,1,66,0.2)",
    zIndex: 1,
    animation: "$spinReverse 30s linear infinite",
    [theme.breakpoints.down("sm")]: { width: "140px", height: "140px" },
  },

  profileImg: {
    width: "155px",
    height: "155px",
    borderRadius: "50%",
    objectFit: "cover",
    position: "relative",
    zIndex: 2,
    animation: "$float 4s ease-in-out infinite",
    boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
    filter: "blur(8px)",
    transition: "filter 0.5s ease",
    [theme.breakpoints.down("sm")]: { width: "120px", height: "120px" },
  },

  profileImgLoaded: { filter: "blur(0)" },

  // ── Stats ──────────────────────────────────────────────────────────────────
  statBox: {
    textAlign: "center",
    padding: "0.5rem 0.8rem",
    border: "1px dashed rgba(105,126,80,0.3)",
    borderRadius: "8px",
    flex: 1,
  },

  statValue: {
    fontFamily: "'Patrick Hand', cursive",
    fontWeight: 700,
    color: "#3A5202",
    lineHeight: 1,
    fontSize: "1.5rem",
  },

  statLabel: {
    fontFamily: "'Patrick Hand', cursive",
    color: "rgba(0,0,0,0.45)",
    fontSize: "0.7rem",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    marginTop: "2px",
  },

  // ── Skills strip ──────────────────────────────────────────────────────────
  skillsStrip: {
    marginTop: "1.6rem",
    paddingTop: "1.2rem",
    paddingBottom: "1.2rem",
    borderTop: "1px dashed rgba(105,126,80,0.28)",
    borderBottom: "1px dashed rgba(105,126,80,0.28)",
    display: "flex",
    flexDirection: "column",
    gap: "0.55rem",
  },

  sectionLabel: {
    fontFamily: "'Patrick Hand', cursive",
    fontSize: "0.65rem",
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "rgba(105,126,80,0.7)",
    fontWeight: 600,
    marginBottom: "0.5rem",
  },

  categoryRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.75rem",
    flexWrap: "wrap",
  },

  categoryLabel: {
    fontFamily: "'Patrick Hand', cursive",
    fontSize: "0.65rem",
    letterSpacing: "0.13em",
    textTransform: "uppercase",
    color: "#697e50",
    fontWeight: 600,
    minWidth: "90px",
    paddingTop: "3px",
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: { minWidth: "70px", fontSize: "0.6rem" },
  },

  dashedDivider: {
    borderTop: "1px dashed rgba(105,126,80,0.28)",
    margin: "0.2rem 0",
  },

  // ── Scroll arrow ──────────────────────────────────────────────────────────
  scrollArrow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "1px",
  },

  scrollText: {
    fontFamily: "'Patrick Hand', cursive",
    fontSize: "0.65rem",
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "rgba(105,126,80,0.6)",
  },

  bounceArrow: {
    color: "rgba(105,126,80,0.55)",
    animation: "$bounce 2s ease-in-out infinite",
    fontSize: "1.6rem !important",
  },

  // ── Inner sections (Education / Certifications) ────────────────────────────
  sectionInner: {
    width: "100%",
    maxWidth: "900px",
  },

  sectionHeaderPill: {
    position: "relative",
    display: "inline-block",
    padding: "0.5rem 1.5rem",
    background: "rgba(255,255,255,0.08)",
    border: "2px dashed #697e50",
    borderRadius: "20px",
    marginTop: "1rem",
    backdropFilter: "blur(4px)",

    "&::before": {
      content: '""',
      position: "absolute",
      top: -4,
      left: -4,
      right: -4,
      bottom: -4,
      border: "1px solid rgba(208,222,199,0.25)",
      borderRadius: "22px",
      zIndex: -1,
    },
  },

  // ── Notepad (Education) ────────────────────────────────────────────────────
  notepad: {
    position: "relative",
    maxWidth: "580px",
    margin: "0 auto",
    width: "100%",
    backgroundColor: "white",
    backgroundImage: `repeating-linear-gradient(
      transparent,
      transparent calc(${LINE_H} - 1px),
      rgba(161,1,66,0.1) calc(${LINE_H} - 1px),
      rgba(161,1,66,0.1) ${LINE_H}
    )`,
    border: "1px solid #d1d5db",
    borderRadius: "4px",
    boxShadow: "3px 4px 0 #d1d5db, 0 6px 20px rgba(0,0,0,0.15)",
    overflow: "hidden",
    paddingLeft: "3rem",
    paddingRight: "1.4rem",
    paddingTop: "1rem",
    paddingBottom: "1.4rem",
  },

  marginLine: {
    position: "absolute",
    left: "2rem",
    top: 0,
    bottom: 0,
    width: "1.5px",
    background: "rgba(161,1,66,0.35)",
  },

  uniImg: {
    borderRadius: "50%",
    flexShrink: 0,
  },

  // ── Certification cards ────────────────────────────────────────────────────
  certGrid: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "1rem",

    [theme.breakpoints.up("sm")]: {
      maxWidth: "580px",
      margin: "0 auto",
      flexDirection: "column",
    },
  },

  certCard: {
    backgroundColor: "#ffffff",
    backgroundImage: "none",
    border: "1px dashed rgba(105,126,80,0.45)",
    borderRadius: "10px",
    padding: "1.2rem 1.4rem",
    display: "flex",
    textAlign: "left",
    gap: "1rem",
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
    transition: "box-shadow 0.2s ease",
    width: "500px",

    "&:hover": {
      boxShadow: "0 6px 22px rgba(0,0,0,0.14)",
    },

    [theme.breakpoints.up("sm")]: {
      width: "100%",
    },
  },

  certBadge: {
    width: "72px",
    height: "72px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  certName: {
    fontFamily: "'Patrick Hand', cursive",
    fontWeight: 700,
    fontSize: "0.95rem",
    color: "#3d3d3d",
    lineHeight: 1.3,
  },

  certMeta: {
    fontFamily: "'Patrick Hand', cursive",
    fontSize: "0.8rem",
    color: "rgba(0,0,0,0.5)",
    marginTop: "2px",
  },

  certLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    fontFamily: "'Patrick Hand', cursive",
    fontSize: "0.72rem",
    color: "#697e50",
    textDecoration: "none",
    marginTop: "4px",
    "&:hover": { color: "#A10142" },
  },

  certEmptyMsg: {
    fontFamily: "'Patrick Hand', cursive",
    color: "rgba(0,0,0,0.35)",
    fontSize: "0.9rem",
    fontStyle: "italic",
    marginTop: "0.5rem",
  },

  // ── Keyframes ──────────────────────────────────────────────────────────────
  "@keyframes float": {
    "0%, 100%": { transform: "translateY(0px)" },
    "50%": { transform: "translateY(-10px)" },
  },
  "@keyframes spin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
  "@keyframes spinReverse": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(-360deg)" },
  },
  "@keyframes blink": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0 } },
  "@keyframes bounce": {
    "0%, 100%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(8px)" },
  },
  "@keyframes pulse": {
    "0%, 100%": { opacity: 1, transform: "scale(1)" },
    "50%": { opacity: 0.45, transform: "scale(0.7)" },
  },
}));

// ─── Component ────────────────────────────────────────────────────────────────

export default function TopHeader() {
  const { classes, cx } = useStyles();
  const typed = useTypewriter(phrases);
  const [imgLoaded, setImgLoaded] = useState(false);

  return (
    <Box>
      {/* ══ Section 1: Hero ════════════════════════════════════════════════ */}
      <Box className={classes.heroSection}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: "easeOut" }}
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <Box className={classes.paperCard}>
            {/* Two-column layout */}
            <Stack direction={{ xs: "column", sm: "row" }} alignItems="stretch">
              {/* Left column */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.1rem",
                  pr: { sm: "2.5rem" },
                  pb: { xs: "2rem", sm: 0 },
                }}
              >
                <Box className={classes.statusBadge}>
                  <span className={classes.dot} />
                  Open to opportunities
                </Box>

                <Box>
                  <Typography className={classes.headlineStatic}>
                    Currently building
                  </Typography>
                  <Typography className={classes.headlineDynamic}>
                    {typed}
                    <span className={classes.cursor} />
                  </Typography>
                </Box>

                <Typography className={classes.bioText}>
                  Frontend engineer based in Malaysia. I build web and mobile
                  apps with React, React Native, and Next.js. Lately diving into
                  cloud and AI-powered tools and what they can do for real
                  products.
                </Typography>

                <Stack direction="row" gap={1.2} flexWrap="wrap" mt="auto">
                  <Button
                    href="#Projects"
                    variant="contained"
                    size="medium"
                    sx={{ fontFamily: "'Patrick Hand', cursive", px: 2.5 }}
                  >
                    See my side work
                  </Button>
                  <Button
                    href="#Connect"
                    variant="outlined"
                    size="medium"
                    sx={{
                      fontFamily: "'Patrick Hand', cursive",
                      px: 2.5,
                      borderColor: "#A10142",
                      color: "#A10142",
                      "&:hover": {
                        bgcolor: "#A10142",
                        color: "#fff",
                        borderColor: "#A10142",
                      },
                    }}
                  >
                    Let's Connect
                  </Button>
                </Stack>
              </Box>

              {/* Vertical divider */}
              <Box className={classes.verticalDivider} />

              {/* Right column */}
              <Box
                sx={{
                  flex: 1,
                  pl: { sm: "2.5rem" },
                  pt: { xs: "2rem", sm: 0 },
                  borderTop: {
                    xs: "1px dashed rgba(105,126,80,0.28)",
                    sm: "none",
                  },
                }}
              >
                <Box className={classes.imageSection}>
                  <Box className={classes.imageWrapper}>
                    <Box className={classes.imageRing} />
                    <Box className={classes.imageRingInner} />
                    <img
                      src={header2}
                      alt="profile illustration"
                      className={cx(
                        classes.profileImg,
                        imgLoaded && classes.profileImgLoaded,
                      )}
                      onLoad={() => setImgLoaded(true)}
                      draggable={false}
                    />
                  </Box>

                  <hr
                    className={classes.dashedDivider}
                    style={{ width: "100%" }}
                  />

                  <Box sx={{ width: "100%" }}>
                    <Typography
                      className={classes.sectionLabel}
                      sx={{ textAlign: "center" }}
                    >
                      At a glance
                    </Typography>
                    <Stack direction="row" gap={1}>
                      {stats.map((s) => (
                        <Box key={s.label} className={classes.statBox}>
                          <Typography className={classes.statValue}>
                            {s.value}
                          </Typography>
                          <Typography className={classes.statLabel}>
                            {s.label}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Stack>

            {/* Skills strip */}
            <Box className={classes.skillsStrip}>
              <Typography className={classes.sectionLabel}>
                Tech stack & tools
              </Typography>
              {skillCategories.map((cat) => (
                <Box key={cat.label} className={classes.categoryRow}>
                  <Typography className={classes.categoryLabel}>
                    {cat.label}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.45}>
                    {cat.items.map((item) => (
                      <Chip
                        key={item}
                        label={item}
                        size="small"
                        sx={{
                          fontSize: "0.67rem",
                          height: 20,
                          bgcolor: "transparent",
                          fontFamily: "'Patrick Hand', cursive",
                          color: "#3A5202",
                          border: "1px solid rgba(105,126,80,0.3)",
                        }}
                      />
                    ))}
                    {cat.overflow && (
                      <Chip
                        label={`+${cat.overflow} more`}
                        size="small"
                        sx={{
                          fontSize: "0.65rem",
                          height: 20,
                          bgcolor: "transparent",
                          fontFamily: "'Patrick Hand', cursive",
                          fontStyle: "italic",
                          color: "rgba(105,126,80,0.6)",
                          border: "1px dashed rgba(105,126,80,0.3)",
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              ))}

              {/* CV download + scroll hint */}
              <Box
                sx={{
                  mt: 1.5,
                  pt: 1.2,
                  borderTop: "1px dashed rgba(105,126,80,0.28)",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <a
                  href={cv}
                  download="Umairatul_Asyiqin_Resume_2026.pdf"
                  style={{ textDecoration: "none" }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      fontFamily: "'Patrick Hand', cursive",
                      fontSize: "0.72rem",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "#A10142",
                      border: "1px dashed rgba(161,1,66,0.55)",
                      borderRadius: "20px",
                      padding: "5px 14px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "#A10142",
                        color: "#fff",
                        borderColor: "#A10142",
                      },
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Download CV
                  </Box>
                </a>
              </Box>
            </Box>

            {/* ══ Section 2: Education ═══════════════════════════════════════════ */}
            <Box mt={10}>
              <Box sx={{ mb: 3, textAlign: "center" }}>
                <Typography
                  variant="subtitle1"
                  className={classes.sectionHeaderPill}
                  sx={{
                    color: "#3A5202",
                    fontFamily: "'Patrick Hand', cursive",
                  }}
                >
                  👩‍💻 Education
                </Typography>
              </Box>
              <Box
                className={classes.notepad}
                data-aos="fade-up"
                data-aos-duration={400}
              >
                <Box className={classes.marginLine} />

                {education.map((res, i) => (
                  <Box key={i}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={1.2}
                      sx={{ height: LINE_H }}
                    >
                      <img
                        src={res.img}
                        alt="university logo"
                        width={26}
                        height={26}
                        className={classes.uniImg}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontFamily: "'Patrick Hand', cursive",
                          fontWeight: 600,
                          color: "#3d3d3d",
                        }}
                      >
                        {res.school}
                      </Typography>
                    </Stack>

                    {[res.major, res.award, res.period].map((line, j) => (
                      <Box
                        key={j}
                        data-aos="fade-up"
                        data-aos-duration={300}
                        data-aos-delay={j * 60}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.55rem",
                          height: LINE_H,
                          fontFamily: "'Patrick Hand', cursive",
                          fontSize: "0.93rem",
                          color: "rgba(0,0,0,0.6)",
                        }}
                      >
                        <span style={{ color: "#A10142", flexShrink: 0 }}>
                          —
                        </span>
                        {line}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>

            <Box mt={11}>
              {/* ══ Section 3: Certifications ══════════════════════════════════════ */}
              <Box className={classes.sectionInner}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="subtitle1"
                    className={classes.sectionHeaderPill}
                    sx={{
                      color: "#3A5202",
                      fontFamily: "'Patrick Hand', cursive",
                    }}
                  >
                    🏅 Certifications
                  </Typography>

                  <Box mt={3}>
                    <Box className={classes.certGrid}>
                      {certifications.map((cert, i) => (
                        <Box
                          key={i}
                          className={classes.certCard}
                          data-aos="fade-up"
                          data-aos-duration={300}
                          data-aos-delay={i * 60}
                        >
                          <Box className={classes.certBadge}>
                            {cert.credlyImg ? (
                              <img
                                src={cert.credlyImg}
                                alt={cert.name}
                                style={{
                                  width: 72,
                                  height: 72,
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              cert.badge || "📜"
                            )}
                          </Box>
                          <Box>
                            <Typography className={classes.certName}>
                              {cert.name}
                            </Typography>
                            <Typography className={classes.certMeta}>
                              {cert.issuer} · {cert.year}
                            </Typography>
                            {cert.link && (
                              <a
                                href={cert.link}
                                target="_blank"
                                rel="noreferrer"
                                className={classes.certLink}
                              >
                                View certificate{" "}
                                <LaunchIcon sx={{ fontSize: "0.75rem" }} />
                              </a>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                    <Typography
                      className={classes.certEmptyMsg}
                      sx={{ mt: 1.5, textAlign: "center" }}
                    >
                      — more certifications coming soon —
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
}
